import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Stack,
  Alert,
  CircularProgress,
} from '@mui/material';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';

const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const getApiBaseUrl = () => {
  if (typeof window !== 'undefined') {
    const runtimeBase = localStorage.getItem('REACT_APP_API_URL');
    if (runtimeBase && runtimeBase.trim()) {
      return runtimeBase.replace(/\/$/, '').replace(/:(\d+)\/api/i, ':3000/api');
    }
  }
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL.replace(/\/$/, '');
  }
  if (
    typeof window !== 'undefined' &&
    (window.location.hostname.includes('ondigitalocean.app') ||
      window.location.hostname.includes('services.knls.in'))
  ) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

export default function CoursePackExportDialog({ open, onClose, courseCode, courseName }) {
  const [includeDeleted, setIncludeDeleted] = useState(false);
  const [includeMedia, setIncludeMedia] = useState(true);
  const [includeEnrolledStudents, setIncludeEnrolledStudents] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');

  const handleExport = async () => {
    if (!courseCode) return;
    setExporting(true);
    setError('');

    try {
      const params = new URLSearchParams({
        includeDeleted: String(includeDeleted),
        includeMedia: String(includeMedia),
        includeEnrolledStudents: String(includeEnrolledStudents),
      });

      const response = await fetch(
        `${getApiBaseUrl()}/lms/courses/${encodeURIComponent(courseCode)}/export?${params.toString()}`,
        { headers: getAuthHeaders() },
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => ({}));
        throw new Error(payload.message || 'Export failed');
      }

      const blob = await response.blob();
      const disposition = response.headers.get('Content-Disposition') || '';
      const match = disposition.match(/filename="?([^"]+)"?/i);
      const fileName = match?.[1] || `${courseCode}.knlcourse`;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      onClose?.();
    } catch (exportError) {
      setError(exportError.message || 'Failed to export course pack');
    } finally {
      setExporting(false);
    }
  };

  return (
    <Dialog open={open} onClose={exporting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Export Course Pack</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            Download a safe backup of <strong>{courseName || courseCode}</strong> including
            subjects, chapters, content, uploaded files, and enrolled students in one{' '}
            <code>.knlcourse</code> zip.
          </Typography>

          <FormControlLabel
            control={
              <Checkbox
                checked={includeDeleted}
                onChange={(event) => setIncludeDeleted(event.target.checked)}
              />
            }
            label="Include deleted chapters & content"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={includeMedia}
                onChange={(event) => setIncludeMedia(event.target.checked)}
              />
            }
            label="Include uploaded media files (PDF, images, documents)"
          />

          <FormControlLabel
            control={
              <Checkbox
                checked={includeEnrolledStudents}
                onChange={(event) => setIncludeEnrolledStudents(event.target.checked)}
              />
            }
            label="Include enrolled students list (restored on import)"
          />

          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={exporting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={18} color="inherit" /> : <CloudDownloadIcon />}
          onClick={handleExport}
          disabled={exporting}
        >
          {exporting ? 'Preparing pack…' : 'Download pack'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
